import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Globe2,
  Loader2,
  MapPin,
  MousePointerClick,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import {
  getAdminLandingOperations,
  type AdminLandingOperationsLocation,
  type AdminLandingOperationsResponse,
  type AdminLandingOperationsServerEvent,
} from '@/lib/api/auth';

const QUICK_WINDOWS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
] as const;

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultFromDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 29);
  return toDateInputValue(date);
};

const getTodayDate = (): string => toDateInputValue(new Date());

const formatNumber = (value: number | null | undefined): string =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return 'Not recorded';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Not recorded';
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatValue = (value: string | null | undefined, fallback = 'Unknown'): string =>
  value && value.trim() ? value : fallback;

const CLICK_ID_SOURCES: Record<string, string> = {
  li_fat_id: 'LinkedIn',
  fbclid: 'Meta',
  gclid: 'Google Ads',
  ttclid: 'TikTok',
  msclkid: 'Bing',
  rdt_cid: 'Reddit',
};

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const getEventSource = (event: AdminLandingOperationsServerEvent): string => {
  const attribution = (event.attribution || {}) as Record<string, unknown>;
  const utmSource = attribution.utm_source;
  if (typeof utmSource === 'string' && utmSource.trim()) {
    return capitalize(utmSource.trim());
  }
  for (const [key, label] of Object.entries(CLICK_ID_SOURCES)) {
    if (typeof attribution[key] === 'string' && (attribution[key] as string).trim()) {
      return label;
    }
  }
  const attributionReferrer = attribution.referrer;
  if (typeof attributionReferrer === 'string' && attributionReferrer.trim()) {
    return attributionReferrer.trim();
  }
  if (event.referrer && event.referrer.trim()) {
    return event.referrer.trim();
  }
  return 'Direct';
};

const formatLocation = (row: Pick<AdminLandingOperationsLocation, 'country' | 'region' | 'city'>): string => {
  const parts = [row.city, row.region, row.country].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Unknown location';
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message || error.response?.data?.error || error.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

const STAT_COLORS = {
  blue: { bg: 'bg-[#eaf4ff]', text: 'text-[#1d6fd6]', ring: 'ring-[#dbeafe]' },
  violet: { bg: 'bg-[#f2eeff]', text: 'text-[#6d4fd6]', ring: 'ring-[#e5deff]' },
  amber: { bg: 'bg-[#fff4e6]', text: 'text-[#c2740c]', ring: 'ring-[#ffe8c7]' },
} as const;

const StatCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  color: keyof typeof STAT_COLORS;
  label: string;
  value: string;
  detail: string;
}> = ({ icon: Icon, color, label, value, detail }) => {
  const palette = STAT_COLORS[color];
  return (
    <div className="rounded-2xl border border-[#e8eef1] bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-[0_4px_16px_rgba(15,23,42,0.06)]">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${palette.bg} ${palette.text} ring-4 ${palette.ring}`}>
          <Icon className="h-5 w-5" />
        </span>
        <p className="text-sm font-medium text-[#5c6b73]">{label}</p>
      </div>
      <p className="mt-4 text-3xl font-bold tracking-tight text-[#101b21]">{value}</p>
      <p className="mt-1.5 text-xs leading-relaxed text-[#8996a0]">{detail}</p>
    </div>
  );
};

const LandingOperationsPage: React.FC = () => {
  const [fromDate, setFromDate] = useState(getDefaultFromDate);
  const [toDate, setToDate] = useState(getTodayDate);
  const [data, setData] = useState<AdminLandingOperationsResponse['data'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSource, setActiveSource] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAdminLandingOperations({
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: 100,
      });
      setData(response.data);
      setActiveSource(null);
    } catch (error) {
      toast({
        title: 'Landing tracking report failed to load',
        description: getErrorMessage(error, 'Unable to load landing page location metrics right now.'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const topLocations = useMemo(() => data?.locations || [], [data?.locations]);
  const serverEvents = useMemo(() => data?.serverEvents || [], [data?.serverEvents]);
  const summary = data?.summary;
  const maxLocationCount = useMemo(
    () => Math.max(1, ...topLocations.map((row) => row.count)),
    [topLocations]
  );

  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    serverEvents.forEach((event) => {
      const source = getEventSource(event);
      counts.set(source, (counts.get(source) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [serverEvents]);

  const filteredEvents = useMemo(
    () => (activeSource ? serverEvents.filter((event) => getEventSource(event) === activeSource) : serverEvents),
    [serverEvents, activeSource]
  );

  const applyQuickWindow = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days + 1);
    setFromDate(toDateInputValue(from));
    setToDate(toDateInputValue(to));
  };

  return (
    <main className="min-h-[calc(100vh-7.5rem)] rounded-[28px] bg-[#f4f8fa] px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight text-[#101b21]">Visitor Traffic & Campaign Tracking</h1>
          <p className="mt-1 text-sm text-[#71828a]">
            See where your visitors and marketing campaigns are coming from.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#e3ecf0] bg-white p-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="inline-flex rounded-lg bg-[#f4f8fa] p-1">
            {QUICK_WINDOWS.map((item) => (
              <button
                key={item.days}
                type="button"
                onClick={() => applyQuickWindow(item.days)}
                className="rounded-md px-3 py-1.5 text-xs font-semibold text-[#53666e] transition-colors hover:bg-white hover:text-[#0e97be] hover:shadow-sm"
              >
                {item.label}
              </button>
            ))}
          </div>
          <Input
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            className="h-9 w-[140px] border-transparent bg-[#f4f8fa] text-xs"
          />
          <span className="text-xs text-[#a4afb6]">to</span>
          <Input
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            className="h-9 w-[140px] border-transparent bg-[#f4f8fa] text-xs"
          />
          <Button type="button" size="sm" onClick={() => void loadReport()} disabled={isLoading} className="ml-1">
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={MousePointerClick}
          color="blue"
          label="Total Visits"
          value={formatNumber(summary?.totalVisits)}
          detail="Total visits recorded in this date range"
        />
        <StatCard
          icon={Globe2}
          color="violet"
          label="Countries"
          value={formatNumber(summary?.countries)}
          detail="Number of different countries visitors came from"
        />
        <StatCard
          icon={Sparkles}
          color="amber"
          label="Campaign & Signup Events"
          value={formatNumber(summary?.serverSideEvents)}
          detail="Events tracked from ad campaigns (LinkedIn, Meta, etc.) and key actions"
        />
      </div>

      <section className="mt-6 rounded-2xl border border-[#e8eef1] bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#8996a0]" />
          <h2 className="text-sm font-semibold text-[#101b21]">Where Visitors Are Located</h2>
        </div>
        <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
          {topLocations.length === 0 ? (
            <p className="text-sm text-[#7a8a91]">No visitor location data has been recorded for this window.</p>
          ) : (
            topLocations.slice(0, 10).map((row) => (
              <div key={`${row.country || 'unknown'}-${row.region || 'unknown'}-${row.city || 'unknown'}`} className="relative overflow-hidden rounded-lg border border-[#eef2f4] px-3 py-2.5">
                <div
                  className="absolute inset-y-0 left-0 bg-[#eaf4ff]"
                  style={{ width: `${Math.max(6, (row.count / maxLocationCount) * 100)}%` }}
                  aria-hidden="true"
                />
                <div className="relative flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[#23343b]">{formatLocation(row)}</p>
                    <p className="text-xs text-[#8c9aa1]">
                      {formatNumber(row.count)} total visits · Last seen {formatDateTime(row.lastSeenAt)}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-bold text-[#101b21]">{formatNumber(row.networkBuckets)}</p>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-[#8c9aa1]">visitors</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="mt-6 overflow-hidden rounded-2xl border border-[#e8eef1] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="border-b border-[#f0f4f6] px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#8996a0]" />
            <div>
              <h2 className="text-sm font-semibold text-[#101b21]">Marketing Campaign Tracking</h2>
              <p className="mt-0.5 text-xs text-[#8996a0]">
                Which ads or marketing campaigns (LinkedIn, Meta, Google, etc.) brought visitors to your site.
              </p>
            </div>
          </div>
          {sourceCounts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setActiveSource(null)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  activeSource === null
                    ? 'bg-[#101b21] text-white'
                    : 'bg-[#f1f5f7] text-[#5c6b73] hover:bg-[#e8eef1]'
                }`}
              >
                All ({serverEvents.length})
              </button>
              {sourceCounts.map(([source, count]) => (
                <button
                  key={source}
                  type="button"
                  onClick={() => setActiveSource(source === activeSource ? null : source)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    activeSource === source
                      ? 'bg-[#101b21] text-white'
                      : 'bg-[#f1f5f7] text-[#5c6b73] hover:bg-[#e8eef1]'
                  }`}
                >
                  {source} ({count})
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-[#a4afb6]">
              <tr>
                <th className="px-5 py-2.5 font-medium">Action Taken</th>
                <th className="px-5 py-2.5 font-medium">Page Visited</th>
                <th className="px-5 py-2.5 font-medium">Came From</th>
                <th className="px-5 py-2.5 font-medium">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#f0f4f6]">
              {filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-sm text-[#8996a0]">
                    {activeSource ? `No events from ${activeSource} for this date range.` : 'No marketing campaign events found for this date range.'}
                  </td>
                </tr>
              ) : (
                filteredEvents.map((event: AdminLandingOperationsServerEvent) => {
                  return (
                    <tr key={event.id} className="align-top transition-colors hover:bg-[#fafcfd]">
                      <td className="whitespace-nowrap px-5 py-3.5 font-semibold text-[#23343b]">{event.eventName}</td>
                      <td className="max-w-[240px] truncate px-5 py-3.5 text-[#5c6b73]">{formatValue(event.eventSourceUrl, 'Unknown URL')}</td>
                      <td className="max-w-[160px] truncate px-5 py-3.5 text-[#5c6b73]">{formatValue(event.referrer, 'Direct / unknown')}</td>
                      <td className="whitespace-nowrap px-5 py-3.5 text-[#8996a0]">{formatDateTime(event.createdAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
};

export default LandingOperationsPage;
